import type { ReactNode } from 'react';
import { useEffect } from 'react';

import { Box } from '#/components/Box/Box';
import { ButtonBar } from '#/components/ButtonBar/ButtonBar';
import { ClientOnly } from '#/containers/ClientOnly';

import { useTrackScrollTrigger } from '#/hooks/useTrackScrollTrigger';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { CmsApiPagePost } from '#/modules/cms/api/types/CmsApiPage';
import { CmsRecommendedJobsBlockLoadable } from '#/modules/cms/blocks/RecommendedJobs/CmsRecommendedJobsBlockLoadable';
import {
  CmsBodyHolder,
  CmsBodyHolderWidth,
} from '#/modules/cms/components/BodyHolder/CmsBodyHolder';
import { CmsButtonBar } from '#/modules/cms/components/ButtonBar/CmsButtonBar';
import { CmsMobileTableOfContentsLoadable } from '#/modules/cms/components/TableOfContents/CmsMobileTableOfContentsLoadable';
import { getCmsApiPageDefaultSearchType } from '#/modules/cms/helpers/getCmsApiPageDefaultSearchType';
import { CmsHelpPageFeedback } from '#/modules/cms/pages/Page/CmsHelpPageFeedback';
import { SearchHeroLoadable } from '#/modules/search/components/Hero/SearchHeroLoadable';
import { FixedStaffToolsLoadable } from '#/modules/staff/components/FixedStaffTools/FixedStaffToolsLoadable';
import { userDashboardApiEnableProfileFeatures } from '#/modules/userDashboard/api/userDashboardApiEnableProfileFeatures';
import { useCurrentUserIsStaff } from '#/store/hooks/useCurrentUserIsStaff';
import { sectionPadding } from '#/theme/theme';
import { showModal } from '#/zustand-stores/modalStore';
import { useUser } from '#/contexts/UserEnvironmentContext';

type Props = { content: CmsApiPagePost; children: ReactNode };

export function CmsPostPageLayout({ content, children }: Props) {
  const { i18n } = useI18n();

  useTrackScrollTrigger('cms-content-end', 'End of Content');

  const isStaff = useCurrentUserIsStaff();
  const { user } = useUser();

  const hero = content.heroSearch?.onlyResults ? undefined : content.heroSearch;

  const showButtonBar =
    content.subsite.canSubscribe || content.hasSocialSharing;

  const bottomTableOfContents = content.tableOfContents;
  const defaultSearchType = getCmsApiPageDefaultSearchType({ content });

  useEffect(() => {
    if (
      content.enableProfileOnVisit &&
      user?.emailVerified &&
      !user.hasIdealistProfileFeature
    ) {
      userDashboardApiEnableProfileFeatures().then(() => {
        showModal('CREATE_USER_PROFILE', {
          title: i18n('Create Your Profile'),
          description: i18n(
            'To continue, please review the fields below and create a public profile.',
          ),
        });
      });
    }
  }, [
    i18n,
    content.enableProfileOnVisit,
    user?.emailVerified,
    user?.hasIdealistProfileFeature,
  ]);

  return (
    <>
      {isStaff && <FixedStaffToolsLoadable content={content} />}

      {bottomTableOfContents && bottomTableOfContents.length > 0 && (
        <ClientOnly>
          <CmsMobileTableOfContentsLoadable
            parents={bottomTableOfContents}
            hasButtonBar={Boolean(showButtonBar)}
          />
        </ClientOnly>
      )}

      {hero && (
        <div style={{ position: 'relative' }}>
          <SearchHeroLoadable
            title={hero.text}
            image={hero.image ?? undefined}
            imageMobile={hero.imageMobile ?? undefined}
            defaultSearchType={defaultSearchType}
          />
        </div>
      )}

      {children}

      {content.hasRecommendedJobs && (
        <Box flex="1 1 auto" width="100%" m={sectionPadding} mt={70}>
          <CmsBodyHolder $width={CmsBodyHolderWidth.NARROW_LAYOUT_WIDE}>
            <CmsRecommendedJobsBlockLoadable
              data={{
                headline: content.recommendedJobsHeadline ?? null,
                layout: 'REGULAR',
                jobFunction: null,
                areaOfFocus: null,
              }}
            />
          </CmsBodyHolder>
        </Box>
      )}

      {showButtonBar && (
        <ButtonBar>
          <CmsButtonBar content={content} />
        </ButtonBar>
      )}
      <CmsHelpPageFeedback
        contentId={content.id}
        contentTitle={content.title}
      />
    </>
  );
}
